/**
 * Gift cart storage — a first-party, strictly-necessary cookie holding the
 * ONE gift the visitor added on /gift so it renders on /cart. The Polar
 * checkout buys a single product, so adding replaces the current item.
 *
 * The cookie carries a display SNAPSHOT taken at add-to-cart time (product
 * id, plan title/subtitle, price text) — the cart page renders straight
 * from it with no CMS lookup, and checkout charges whatever Polar actually
 * prices the product id at. Contents are product facts only — NO personal
 * data — which keeps the cookie in the ePrivacy "strictly necessary"
 * consent exemption (it exists solely to deliver a service the user
 * explicitly requested). Disclosure tracking:
 * documentation/compliance-open-issues.md.
 */

export const GIFT_CART_COOKIE = "fulton_gift_cart";
const MAX_AGE_SECONDS = 7 * 24 * 60 * 60; // 7 days

export interface GiftCartItem {
  polarProductId: string;
  /** Display snapshot from the PDP at add time. */
  planTitle?: string;
  planSubtitle?: string;
  /** Price text as the buyer saw it, e.g. "$72". */
  price?: string;
  strikeoutPrice?: string;
  addedAt: number;
}

/** The raw (decoded) cookie value, or null when absent. Split from parsing
 *  so React's useSyncExternalStore can snapshot a stable primitive. */
export function readGiftCartRaw(): string | null {
  if (typeof document === "undefined") return null;
  try {
    const raw = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${GIFT_CART_COOKIE}=`))
      ?.slice(GIFT_CART_COOKIE.length + 1);
    return raw ? decodeURIComponent(raw) : null;
  } catch {
    return null;
  }
}

export function parseGiftCart(raw: string | null | undefined): GiftCartItem | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<GiftCartItem>;
    if (typeof parsed?.polarProductId !== "string" || !parsed.polarProductId.trim()) {
      return null;
    }
    return {
      polarProductId: parsed.polarProductId,
      planTitle: typeof parsed.planTitle === "string" ? parsed.planTitle : undefined,
      planSubtitle: typeof parsed.planSubtitle === "string" ? parsed.planSubtitle : undefined,
      price: typeof parsed.price === "string" ? parsed.price : undefined,
      strikeoutPrice:
        typeof parsed.strikeoutPrice === "string" ? parsed.strikeoutPrice : undefined,
      addedAt: typeof parsed.addedAt === "number" ? parsed.addedAt : 0,
    };
  } catch {
    return null;
  }
}

export function readGiftCart(): GiftCartItem | null {
  return parseGiftCart(readGiftCartRaw());
}

export function writeGiftCart(item: Omit<GiftCartItem, "addedAt">): void {
  if (typeof document === "undefined") return;
  try {
    const value = encodeURIComponent(
      JSON.stringify({ ...item, addedAt: Date.now() } satisfies GiftCartItem),
    );
    document.cookie = `${GIFT_CART_COOKIE}=${value}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax`;
  } catch {
    // Best-effort — a blocked cookie just means an empty cart page.
  }
}

export function clearGiftCart(): void {
  if (typeof document === "undefined") return;
  try {
    document.cookie = `${GIFT_CART_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    // Best-effort.
  }
}
